"use client";

import { useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Home, Zap, ReceiptText, UserRound, Headset, LogIn, LogOut, LayoutGrid, Ticket } from "lucide-react";
import QuickListDialog from "@/components/lista/quick-list-dialog";
import AuthDialog from "@/components/auth/auth-dialog";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useAuthWithLocationSync } from "@/hooks/use-auth-with-location-sync";
import FreeDeliveryProgress from "@/components/cart/free-delivery-progress";
import Image from "next/image";

const navItems = [
  { label: "Calii", href: "/", icon: Home },
  // { label: "Categorías", href: "/ver-categorias", icon: LayoutGrid },
  // { label: "Lista rápida", href: "/lista", icon: Zap }, // TODO
  { label: "Pedidos", href: "/pedidos", icon: ReceiptText, authGated: true },
  { label: "Chat con soporte", href: "/soporte", icon: Headset },
  { label: "Cuenta", href: "/cuenta", icon: UserRound, authGated: true },
  { label: "Cupones", href: "/cuenta/cupones", icon: Ticket, authGated: true },
];

function isActive(tabHref: string, pathname: string) {
  if (tabHref === "/") return pathname === "/" || (pathname.startsWith("/categorias") && !pathname.startsWith("/ver-categorias"));
  if (pathname.startsWith("/cuenta/cupones")) return tabHref === "/cuenta/cupones";
  return pathname.startsWith(tabHref);
}

// ShadCN sidebar examples: https://ui.shadcn.com/blocks/sidebar#sidebar-11
export default function AppSidebar({ headerHeight = 64, collectionHierarchy, categorySlug = '0', subcategorySlug = '0' }: { headerHeight?: number, collectionHierarchy: any[], categorySlug: string , subcategorySlug: string }) {
  const pathname = usePathname();
  const { user, isAuthenticated, logout } = useAuthWithLocationSync();
  const [quickListOpen, setQuickListOpen] = useState(false);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [authRedirectTo, setAuthRedirectTo] = useState<string | undefined>();

  const initial = user?.first_name?.charAt(0)?.toUpperCase() || "U";
  const fullName = user ? `${user.first_name} ${user.last_name}`.trim() : "";

  return (
    <>
    <Sidebar
      collapsible="icon"
      style={{
        top: `${headerHeight}px`,
        height: `calc(100svh - ${headerHeight}px)`,
        bottom: "auto",
      }}
    >
      <SidebarContent>
        <FreeDeliveryProgress className="px-3 py-3 group-data-[collapsible=icon]:hidden" />
        <Separator />
        <SidebarGroup>
          <SidebarGroupContent className="mb-2">
            <SidebarMenu>
              {navItems.map((item) => {
                const useButton = item.href === "/lista" || (item.authGated && !isAuthenticated);
                return (
                  <SidebarMenuItem key={item.href}>
                    {useButton ? (
                      <SidebarMenuButton
                        isActive={isActive(item.href, pathname)}
                        tooltip={item.label}
                        onClick={() => {
                          if (item.href === "/lista") {
                            setQuickListOpen(true);
                          } else {
                            setAuthRedirectTo(item.href);
                            setAuthDialogOpen(true);
                          }
                        }}
                        className="cursor-pointer"
                      >
                        <item.icon />
                        <span>{item.label}</span>
                      </SidebarMenuButton>
                    ) : (
                      <SidebarMenuButton
                        asChild
                        isActive={isActive(item.href, pathname)}
                        tooltip={item.label}
                      >
                        <Link href={item.href}>
                          <item.icon />
                          <span>{item.label}</span>
                        </Link>
                      </SidebarMenuButton>
                    )}
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
          <Separator />
          <SidebarGroupContent className="mt-2">
            <SidebarGroupLabel className="">Categorías</SidebarGroupLabel>
            {collectionHierarchy.filter((item) => !(parseInt(item.id) === -1 && !isAuthenticated)).map((item) => (
                <SidebarMenuItem key={item.id}>
                  <SidebarMenuButton
                    asChild
                    isActive={item.id.toString() === categorySlug}
                    tooltip={item.name}
                  >
                    <Link href={`/categorias/${item.id}`}>
                      <Image src={item.preview_image_url || 'https://caliiorderissues.s3.us-east-2.amazonaws.com/Promos%20-%202.png'} alt={item.name} width={20} height={20} />
                      <span>{item.name}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <Separator />
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton tooltip={fullName} className="!h-auto py-2 cursor-pointer">
                    <Avatar className="h-6 w-6 shrink-0 rounded-full">
                      <AvatarFallback className="bg-gray-300 text-muted-foreground text-xs font-medium">
                        {initial}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col leading-tight min-w-0">
                      <span className="text-sm font-medium truncate">{fullName}</span>
                      <span className="text-xs text-muted-foreground truncate">{user?.email}</span>
                    </div>
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="top" align="start" className="w-56">
                  <DropdownMenuItem asChild className="cursor-pointer">
                    <Link href="/cuenta">Cuenta</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="cursor-pointer">
                    <Link href="/pedidos">Pedidos</Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="cursor-pointer text-red-600">
                    <LogOut className="mr-2 h-4 w-4" />
                    Cerrar sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <SidebarMenuButton
                tooltip="Iniciar sesión"
                onClick={() => setAuthDialogOpen(true)}
                className="cursor-pointer"
              >
                <LogIn />
                <span>Iniciar sesión</span>
              </SidebarMenuButton>
            )}
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
    <QuickListDialog open={quickListOpen} onOpenChange={setQuickListOpen} />
    <AuthDialog open={authDialogOpen} onOpenChange={setAuthDialogOpen} redirectTo={authRedirectTo} />
    </>
  );
}
